import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, CurrencyInr, CheckCircle, XCircle, Warning, Clock,
  DownloadSimple, Receipt, ShieldCheck, ChartBar
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const VO_STATUS_COLORS = {
  'Approved': 'bg-green-100 text-green-800',
  'Rejected': 'bg-red-100 text-red-800',
  'Pending': 'bg-amber-100 text-amber-800',
};

export default function ClientSnapshot() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/client-snapshot/${token}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'This snapshot link is no longer valid or has expired.' : 'Unable to load project snapshot. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client-snapshot/${token}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Project_Snapshot.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* silently fail, user can retry */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-pulse text-zinc-500 text-sm">Loading project snapshot...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="bg-white border border-zinc-200 p-10 text-center max-w-sm">
          <XCircle size={40} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }

  const { project, company, client, commercial, variation_orders, financial, invoices, client_po } = data;
  const hasOverdue = financial.overdue > 0;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="client-snapshot-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{company.name}</p>
            <h1 className="text-base font-bold text-zinc-900 mt-0.5">Project Snapshot</h1>
          </div>
          <button
            onClick={handleDownloadPdf}
            className="flex items-center gap-1.5 bg-zinc-900 text-white px-3 py-1.5 text-xs font-medium hover:bg-zinc-800 transition-colors"
            data-testid="download-snapshot-pdf"
          >
            <DownloadSimple size={14} /> Download PDF
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Project Overview */}
        <section className="bg-white border border-zinc-200 p-5" data-testid="snapshot-overview">
          <SectionTitle icon={FileText}>Project Overview</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <InfoBlock label="Project Reference" value={project.pi_number} />
            {project.project_code && <InfoBlock label="Project Code" value={project.project_code} />}
            <InfoBlock label="Client" value={client.name} />
            <InfoBlock label="Status" value={project.status} highlight />
          </div>
        </section>

        {/* Commercial Summary */}
        <section className="bg-white border border-zinc-200 p-5" data-testid="snapshot-commercial">
          <SectionTitle icon={ChartBar}>Commercial Summary</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <ValueCard label="Approved BOQ Value" value={fmt(commercial.boq_value)} />
            <ValueCard label="Approved Variation Orders" value={fmt(commercial.approved_vo_value)} />
            <ValueCard label="Total Project Value" value={fmt(commercial.total_project_value)} primary />
          </div>
          {commercial.latest_revision && (
            <p className="text-[10px] text-zinc-400 mt-3">Latest Revision: {commercial.latest_revision}</p>
          )}
        </section>

        {/* Variation Orders */}
        {variation_orders.length > 0 && (
          <section className="bg-white border border-zinc-200 p-5" data-testid="snapshot-vos">
            <SectionTitle icon={FileText}>Variation Orders</SectionTitle>
            <table className="w-full mt-4 text-sm">
              <thead>
                <tr className="text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
                  <th className="text-left pb-2">Description</th>
                  <th className="text-right pb-2">Value</th>
                  <th className="text-center pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {variation_orders.map((vo, idx) => (
                  <tr key={idx}>
                    <td className="py-2.5 text-xs text-zinc-700">{vo.description}</td>
                    <td className="py-2.5 text-xs text-right font-medium">{fmt(vo.value)}</td>
                    <td className="py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-sm ${VO_STATUS_COLORS[vo.status] || 'bg-zinc-100 text-zinc-600'}`}>
                        {vo.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Financial Summary */}
        <section className="bg-white border border-zinc-200 p-5" data-testid="snapshot-financial">
          <SectionTitle icon={CurrencyInr}>Financial Summary</SectionTitle>
          <div className="mt-4 space-y-0 divide-y divide-zinc-100">
            <FinRow label="Total Project Value" value={fmt(financial.total_project_value)} />
            <FinRow label="Total Billed" value={fmt(financial.total_billed)} />
            <FinRow label="Total Received" value={fmt(financial.total_received)} className="text-green-700 font-bold" />
            <FinRow label="Outstanding" value={fmt(financial.outstanding)} bold />
            {hasOverdue && (
              <div className="flex justify-between items-center py-3 bg-red-50 px-3 -mx-5 border-t-2 border-red-200">
                <span className="text-xs font-bold text-red-700 flex items-center gap-1.5">
                  <Warning size={14} weight="fill" /> Overdue (&gt;30 days)
                </span>
                <span className="text-sm font-bold text-red-700">{fmt(financial.overdue)}</span>
              </div>
            )}
          </div>
        </section>

        {/* Invoices */}
        {invoices.length > 0 && (
          <section className="bg-white border border-zinc-200 p-5" data-testid="snapshot-invoices">
            <SectionTitle icon={Receipt}>Invoices</SectionTitle>
            <table className="w-full mt-4 text-sm">
              <thead>
                <tr className="text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
                  <th className="text-left pb-2">Invoice #</th>
                  <th className="text-left pb-2">Date</th>
                  <th className="text-right pb-2">Amount</th>
                  <th className="text-center pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {invoices.map((inv, idx) => (
                  <tr key={idx}>
                    <td className="py-2.5 text-xs font-semibold text-zinc-800">{inv.invoice_number}</td>
                    <td className="py-2.5 text-xs text-zinc-500">{inv.date}</td>
                    <td className="py-2.5 text-xs text-right font-medium">{fmt(inv.amount)}</td>
                    <td className="py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-sm ${
                        inv.status === 'Sent' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>{inv.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Client PO Status */}
        <section className="bg-white border border-zinc-200 p-5" data-testid="snapshot-po">
          <SectionTitle icon={ShieldCheck}>Purchase Order Status</SectionTitle>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <InfoBlock label="PO Number" value={client_po.po_number || 'Not Provided'} />
            <InfoBlock
              label="Status"
              value={client_po.status === 'Completed' || client_po.status === 'Overridden' ? 'Received' : 'Pending'}
              highlight={client_po.status === 'Completed' || client_po.status === 'Overridden'}
            />
          </div>
        </section>

        {/* Footer */}
        <div className="text-center text-[10px] text-zinc-400 py-4">
          Generated by {company.name} Project Management System &nbsp;|&nbsp; Confidential
        </div>
      </main>
    </div>
  );
}


/* ===== Sub-components ===== */

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2">
    <Icon size={18} weight="fill" className="text-zinc-400" />
    <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500">{children}</h2>
  </div>
);

const InfoBlock = ({ label, value, highlight }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
    <p className={`text-sm font-semibold mt-0.5 ${highlight ? 'text-blue-700' : 'text-zinc-800'}`}>{value}</p>
  </div>
);

const ValueCard = ({ label, value, primary }) => (
  <div className={`border p-3.5 ${primary ? 'border-blue-200 bg-blue-50' : 'border-zinc-200 bg-zinc-50'}`}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
    <p className={`text-lg font-bold mt-1 ${primary ? 'text-blue-700' : 'text-zinc-800'}`}>{value}</p>
  </div>
);

const FinRow = ({ label, value, bold, className = '' }) => (
  <div className="flex justify-between items-center py-2.5">
    <span className={`text-xs ${bold ? 'font-bold text-zinc-900' : 'text-zinc-600'}`}>{label}</span>
    <span className={`text-sm ${bold ? 'font-bold text-zinc-900' : 'font-medium text-zinc-700'} ${className}`}>{value}</span>
  </div>
);
